<template>
    <!-- 外容器：3D 效果 & 淡入 -->
    <div class="profile-container" ref="addressBlock">
      <div class="profile-card single-column">
  
        <div class="profile-header">
          <h1>寄件地址</h1>
          <p>在此可新增、修改並管理「宅配住家地址」與「超商地址」。</p>
        </div>
  
        <!-- 編輯表單 -->
        <form class="profile-edit-form" @submit.prevent="updateAddresses">
  
          <div class="form-group">
            <label>地址清單 (每種方式可獨立設預設)</label>
  
            <!-- 每筆地址 -->
            <div
              v-for="(address, index) in addresses"
              :key="index"
              class="address-block"
            >
              <!-- 運送類型 -->
              <div class="row-wrap">
                <select
                  class="shipping-type"
                  v-model="address.shipping_type"
                  @change="onTypeChange(index)"
                >
                  <option value="">選擇寄件類型</option>
                  <option value="home">住家 (宅配)</option>
                  <option value="seven">7-11</option>
                  <option value="family">全家</option>
                  <option value="hilife">萊爾富</option>
                </select>
              </div>
  
              <!-- 住家 -->
              <div v-if="address.shipping_type === 'home'">
                <div class="row-wrap">
                  <!-- 縣市 -->
                  <select
                    class="city-select"
                    v-model="address.city"
                    @change="onCityChange(index)"
                  >
                    <option value="">選擇縣市</option>
                    <option
                      v-for="cityItem in twDistricts"
                      :key="cityItem.CityName"
                      :value="cityItem.CityName"
                    >
                      {{ cityItem.CityName }}
                    </option>
                  </select>
  
                  <!-- 區域 -->
                  <select
                    class="district-select"
                    v-model="address.district"
                    @change="onDistrictChange(index)"
                  >
                    <option value="">選擇地區</option>
                    <option
                      v-for="area in getDistricts(address.city)"
                      :key="area.AreaName"
                      :value="area.AreaName"
                    >
                      {{ area.AreaName }}
                    </option>
                  </select>
  
                  <!-- 郵遞區號 (自動帶出) -->
                  <select class="zip-select" v-model="address.zip" disabled>
                    <option :value="address.zip">
                      {{ address.zip ? address.zip : '郵遞區號' }}
                    </option>
                  </select>
                </div>
  
                <!-- 詳細街巷弄門牌 -->
                <input
                  class="street-input"
                  type="text"
                  v-model="address.street"
                  placeholder="地址：巷弄門牌"
                />
              </div>
  
              <!-- 超商 -->
              <div
                v-else-if="['seven','family','hilife'].includes(address.shipping_type)"
                class="row-wrap"
              >
                <input
                  type="text"
                  class="store-id-input"
                  v-model="address.store_id"
                  placeholder="店號"
                />
                <input
                  type="text"
                  class="store-name-input"
                  v-model="address.store_name"
                  placeholder="店名"
                />
                <a
                  class="store-link"
                  :href="getStoreLink(address.shipping_type)"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  查看{{ getStoreLabel(address.shipping_type) }}
                </a>
              </div>
  
              <!-- 按鈕群組: 預設 / 刪除 -->
              <div class="btn-group">
                <label class="default-label">
                  <!-- 只在「同 shipping_type」之間互斥 -->
                  <input
                    type="radio"
                    class="custom-radio"
                    :checked="address.is_default === 1"
                    @change="setAsDefault(index)"
                  />
                  <span class="radio-design"></span>
                  設為預設
                </label>
  
                <button
                  type="button"
                  class="remove-btn"
                  @click="removeAddress(index)"
                >
                  刪除
                </button>
              </div>
  
            </div>
  
            <!-- 新增地址 -->
            <button type="button" @click="addAddress">
              新增地址
            </button>
          </div>
  
          <!-- 錯誤訊息 -->
          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>
  
          <!-- 提交: 更新後端 -->
          <button type="submit" class="submit-btn">
            {{ isSending ? '更新中...' : '更新' }}
          </button>
        </form>
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { twDistricts } from './twalladdress.js'; // 您的台灣縣市區域資料
  import { mapState, mapMutations } from 'vuex';
  import Swal from 'sweetalert2';
  
  export default {
    name: "AddressPage",
    data() {
      return {
        addresses: [],
        errorMessage: '',
        isSending: false,
        twDistricts
      };
    },
    computed: {
      ...mapState(['userProfile']),
    },
    methods: {
      ...mapMutations(['setUserProfile']),
  
      // 切換 shipping_type：清空不需要的欄位
      onTypeChange(index) {
        const addr = this.addresses[index];
        if (addr.shipping_type === 'home') {
          // 清空超商欄位
          addr.store_id = '';
          addr.store_name = '';
        } else {
          // 清空住家欄位
          addr.city = '';
          addr.district = '';
          addr.zip = '';
          addr.street = '';
        }
      },
      // 使用者選擇了「縣市」時，區/郵遞區號先清空
      onCityChange(index) {
        this.addresses[index].district = '';
        this.addresses[index].zip = '';
      },
      // 使用者選擇了「區」時，自動帶出 zip
      onDistrictChange(index) {
        const a = this.addresses[index];
        const cityObj = this.twDistricts.find(item => item.CityName === a.city);
        if (cityObj) {
          const areaObj = cityObj.AreaList.find(ar => ar.AreaName === a.district);
          a.zip = areaObj ? areaObj.ZipCode : '';
        } else {
          a.zip = '';
        }
      },
      // 取得該城市的區域列表
      getDistricts(cityName) {
        const cityObj = this.twDistricts.find(item => item.CityName === cityName);
        return cityObj ? cityObj.AreaList : [];
      },
  
      // 超商連結
      getStoreLink(type) {
        if (type === 'seven') return 'https://emap.pcsc.com.tw/';
        if (type === 'family') return 'https://www.family.com.tw/Marketing/zh/Map';
        if (type === 'hilife') return 'https://www.hilife.com.tw/storeInquiry_pokemon.aspx';
        return '#';
      },
      getStoreLabel(type) {
        if (type === 'seven') return '7-11';
        if (type === 'family') return '全家';
        if (type === 'hilife') return '萊爾富';
        return '超商';
      },
  
      // 新增一筆地址
      addAddress() {
        if (this.addresses.length >= 10) {
          this.errorMessage = '最多只能新增 10 筆地址';
          return;
        }
        this.errorMessage = '';
        this.addresses.push({
          shipping_type: '',
          city: '',
          district: '',
          zip: '',
          street: '',
          store_id: '',
          store_name: '',
          is_default: 0
        });
      },
      // 刪除
      removeAddress(index) {
        if (this.addresses.length <= 1) {
          this.errorMessage = '至少保留 1 筆地址';
          return;
        }
        this.errorMessage = '';
        this.addresses.splice(index, 1);
      },
  
      // 設為預設：只在相同 shipping_type 互斥
      setAsDefault(index) {
        const curType = this.addresses[index].shipping_type;
        // 先把相同 shipping_type 的都歸零
        this.addresses.forEach(addr => {
          if (addr.shipping_type === curType) {
            addr.is_default = 0;
          }
        });
        // 再把目前這筆設為 1
        this.addresses[index].is_default = 1;
      },
  
      // 提交到後端
      async updateAddresses() {
        this.isSending = true;
        this.errorMessage = '';
  
        try {
          // 前端檢查
          for (let i = 0; i < this.addresses.length; i++) {
            const a = this.addresses[i];
            if (!a.shipping_type) {
              this.errorMessage = `第 ${i+1} 筆需選寄件類型`;
              this.isSending = false;
              return;
            }
            if (a.shipping_type === 'home') {
              if (!a.city || !a.district || !a.street) {
                this.errorMessage = `第 ${i+1} 筆住家資訊不完整 (city/district/street)`;
                this.isSending = false;
                return;
              }
            } else if (['seven','family','hilife'].includes(a.shipping_type)) {
              if (!a.store_name) {
                this.errorMessage = `第 ${i+1} 筆超商地址 (店名) 不可為空`;
                this.isSending = false;
                return;
              }
            }
          }

          console.log('更新的地址', this.addresses);
  
          // 呼叫後端
          const res = await axios.post('/api/user/updateAddresses', {
            user_id: this.userProfile.user_id,
            addresses: this.addresses
          });
  
          if (res.data.success) {
            // 同步更新 Vuex
            this.userProfile.addresses = JSON.parse(JSON.stringify(this.addresses));
            this.setUserProfile(this.userProfile);
  
            Swal.fire({
              icon: 'success',
              title: '更新成功！',
              text: '您的地址已更新',
              timer: 2000
            });
          } else {
            this.errorMessage = res.data.message || '更新失敗，請稍後再試';
          }
        } catch (err) {
          console.error(err);
          this.errorMessage = err.response?.data?.message || '伺服器錯誤，請稍後再試';
        } finally {
          this.isSending = false;
        }
      }
    },
    mounted() {
      // 淡入 + 3D翻轉
      const container = this.$refs.addressBlock;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add("container-visible");
            }
          });
        },
        { threshold: 0.1 }
      );
      observer.observe(container);
  
      // 若 Vuex 有地址就帶入
      if (this.userProfile && Array.isArray(this.userProfile.addresses)) {
        this.addresses = JSON.parse(JSON.stringify(this.userProfile.addresses));
        // 若沒地址, 可預設一筆
        if (!this.addresses.length) {
          this.addresses.push({
            shipping_type: '',
            city: '',
            district: '',
            zip: '',
            street: '',
            store_id: '',
            store_name: '',
            is_default: 0
          });
        }
      } else {
        // 若 userProfile 沒 addresses
        this.addresses = [
          {
            shipping_type: '',
            city: '',
            district: '',
            zip: '',
            street: '',
            store_id: '',
            store_name: '',
            is_default: 0
          }
        ];
      }
    }
  };
  </script>
  
  <style scoped>
  /* ===== 容器: 3D + 淡入 ===== */
  .profile-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    min-width: 800px;
  
    opacity: 0;
    transform: translateY(50px) rotateX(8deg);
    transform-origin: top;
    transition: opacity 1.2s ease, transform 1.2s ease;
    perspective: 800px;
  }
  
  .profile-container.container-visible {
    opacity: 1;
    transform: translateY(0) rotateX(0);
  }
  
  .profile-card {
    background-color: #f9f9f9;
    border-radius: 10px;
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.1);
    padding: 30px;
  
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.8s ease, transform 0.8s ease;
  }
  .container-visible .profile-card {
    opacity: 1;
    transform: translateY(0);
  }
  
  .single-column {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  
  .profile-header {
    text-align: center;
  }
  .profile-header h1 {
    font-size: 2rem;
    margin-bottom: 10px;
  }
  .profile-header p {
    line-height: 1.6;
    font-size: 1rem;
    color: #666;
  }
  
  .profile-edit-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  
  .form-group label {
    font-weight: bold;
    margin-bottom: 5px;
    display: block;
  }
  .error-message {
    color: #c0392b;
    font-weight: bold;
  }
  
  /* ========== 地址區塊 ========== */
  .address-block {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-bottom: 20px;
    border: 1px solid #eee;
    padding: 15px;
    border-radius: 8px;
    background: #fff;
    min-height: 160px;
  }
  
  .row-wrap {
    display: flex;
    gap: 10px;
  }
  
  input[type="text"],
  select {
    border: 1px solid #000;
    border-radius: 5px;
    padding: 8px;
    font-size: 1rem;
    color: #333;
    background-color: #fff;
    box-sizing: border-box;
  }
  input[type="text"]:focus,
  select:focus {
    outline: none;
    box-shadow: none;
    border-color: #000;
  }
  input[type="text"]:hover,
  select:hover {
    border-color: #000;
  }
  
  .shipping-type {
    flex: 0 0 150px;
  }
  .city-select,
  .district-select,
  .zip-select {
    flex: 1;
  }
  .street-input {
    width: 100%;
    margin-top: 10px;
  }
  .store-id-input,
  .store-name-input {
    flex: 1;
  }
  
  /* 預設 / 刪除 */
  .btn-group {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  /* radio */
  .custom-radio {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    width: 0; height: 0;
    margin: 0;
  }
  .default-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    position: relative;
  }
  .radio-design {
    display: inline-block;
    width: 18px; height: 18px;
    background-color: #fff;
    border: 2px solid #666;
    border-radius: 50%;
    position: relative;
    box-sizing: border-box;
    transition: all 0.2s ease;
  }
  .default-label:hover .radio-design {
    border-color: #3498db;
  }
  .custom-radio:checked + .radio-design {
    border-color: #3498db;
    background-color: #3498db;
  }
  .custom-radio:checked + .radio-design::after {
    content: "";
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: 8px; height: 8px;
    border-radius: 50%;
    background: #fff;
  }
  
  /* 刪除按鈕 */
  .remove-btn {
    padding: 6px 12px;
    background-color: #e74c3c;
    color: #fff;
    border: none;
    border-radius: 5px;
    cursor: pointer;
  }
  .remove-btn:hover {
    background-color: #c0392b;
  }
  
  /* 新增地址 (藍色) */
  .form-group>button {
    padding: 8px 15px;
    border-radius: 5px;
    background-color: #2980b9;
    color: #fff;
    border: none;
    cursor: pointer;
  }
  .form-group>button:hover {
    background-color: #3498db;
  }
  
  /* 提交 (綠色) */
  .submit-btn {
    display: inline-block;
    padding: 10px 20px;
    background-color: #27ae60;
    color: #fff;
    border: none;
    border-radius: 5px;
    cursor: pointer;
  }
  .submit-btn:hover {
    background-color: #1a743f;
    transform: scale(1.05);
  }
  
  /* 超商地圖連結 */
  .store-link {
    display: inline-block;
    margin-left: 10px;
    padding: 6px 12px;
    background-color: #3498db;
    color: #fff;
    text-decoration: none;
    border-radius: 5px;
    font-size: 0.9rem;
    cursor: pointer;
  }
  .store-link:hover {
    background-color: #2980b9;
  }
  
  /* 小於 850px RWD: 直排 */
  @media (max-width: 850px) {
    .profile-container {
      min-width: auto;
      width: 90%;
    }
    .row-wrap {
      flex-direction: column;
    }
    .shipping-type,
    .city-select,
    .district-select,
    .zip-select,
    .street-input,
    .store-id-input,
    .store-name-input {
      flex: unset;
      width: 100%;
    }
  }
  </style>
  